// Redesign 2026 – /vydane-dluhopisy: hledání emise, filtr vydané/splacené a rozbalení dlouhých skupin.
//
// Bez JS jsou hledání a filtr schované (`hidden`), rozcestník programů funguje jako obyčejné kotvy
// a <noscript> v šabloně ukáže všechny emise. Filtruje se v prohlížeči – emisí jsou stovky, ne tisíce.
//
// Markup (page-rd-vydane-dluhopisy.php, template-parts/redesign/vydane/*):
//   [data-rd-vd]                    obal přehledu
//   [data-rd-vd-part="vydane|splacene"]   sekce, v ní skupiny [data-rd-vd-group] (id = kotva)
//   [data-rd-vd-item]               buňka emise, [data-rd-vd-rest] = za limitem, [data-rd-vd-name] = název
//   [data-rd-vd-more]               tlačítko rozbalení skupiny (data-label-show / data-label-hide)
//   [data-rd-vd-input], [data-rd-vd-filter], [data-rd-vd-index-item] … levý panel (tools.php)

const ACTIVE = 'is-active';

// Hledá se bez ohledu na diakritiku, velikost písmen a druh pomlčky (názvy mají i nezlomitelnou „e‑Finance“).
const normalize = (text) => text
  .normalize('NFD')
  .replace(/[̀-ͯ]/g, '')
  .replace(/[‐-―]/g, '-')
  .toLowerCase()
  .trim();

// Stejné skloňování jako efi_rd_issues_count_label() v issued-bonds.php.
const countLabel = (count) => `${count} ${count >= 1 && count <= 4 ? 'emise' : 'emisí'}`;

export default function initRedesignVydane() {
  const root = document.querySelector('[data-rd-vd]');
  if (!root) {
    return;
  }

  const search = root.querySelector('[data-rd-vd-search]');
  const input = root.querySelector('[data-rd-vd-input]');
  const found = root.querySelector('[data-rd-vd-found]');
  const foundCount = root.querySelector('[data-rd-vd-found-count]');
  const status = root.querySelector('[data-rd-vd-status]');
  const filters = [...root.querySelectorAll('[data-rd-vd-filter]')];
  const empty = root.querySelector('[data-rd-vd-empty]');
  const emptyQuery = root.querySelector('[data-rd-vd-empty-query]');

  const parts = [...root.querySelectorAll('[data-rd-vd-part]')].map((el) => ({
    el,
    status: el.dataset.rdVdPart,
    index: root.querySelector(`[data-rd-vd-index="${el.dataset.rdVdPart}"]`),
  }));

  const groups = [...root.querySelectorAll('[data-rd-vd-group]')].map((el) => {
    const indexItem = root.querySelector(`[data-rd-vd-index-item="${el.id}"]`);

    return {
      el,
      status: el.closest('[data-rd-vd-part]')?.dataset.rdVdPart || '',
      open: false,
      count: el.querySelector('[data-rd-vd-count]'),
      more: el.querySelector('[data-rd-vd-more]'),
      indexItem,
      indexCount: indexItem?.querySelector('[data-rd-vd-index-count]') || null,
      items: [...el.querySelectorAll('[data-rd-vd-item]')].map((item) => ({
        el: item,
        rest: item.hasAttribute('data-rd-vd-rest'),
        key: normalize(item.querySelector('[data-rd-vd-name]')?.textContent || item.textContent),
      })),
    };
  });

  const state = { words: [], query: '', status: '' };

  const apply = () => {
    const searching = state.words.length > 0;
    let total = 0;

    groups.forEach((group) => {
      const statusMatch = state.status === '' || state.status === group.status;
      let matches = 0;

      group.items.forEach((item) => {
        const match = statusMatch && (!searching || state.words.every((word) => item.key.includes(word)));
        if (match) {
          matches += 1;
        }
        // při hledání se ukazují všechny shody, jinak jen buňky do limitu (nebo celá rozbalená skupina)
        item.el.hidden = !match || (!searching && item.rest && !group.open);
      });

      total += matches;
      group.el.hidden = matches === 0;

      if (group.count) {
        group.count.textContent = countLabel(matches);
      }
      if (group.more) {
        group.more.hidden = searching || matches === 0;
      }
      if (group.indexItem) {
        group.indexItem.hidden = matches === 0;
      }
      if (group.indexCount) {
        group.indexCount.textContent = String(matches);
      }
    });

    parts.forEach((part) => {
      const visible = groups.some((group) => group.status === part.status && !group.el.hidden);
      part.el.hidden = !visible;
      if (part.index) {
        part.index.hidden = !visible;
      }
    });

    filters.forEach((button) => {
      const isActive = button.dataset.rdVdFilter === state.status;
      button.classList.toggle(ACTIVE, isActive);
      button.setAttribute('aria-pressed', isActive ? 'true' : 'false');
    });

    if (found && foundCount) {
      found.hidden = !searching;
      foundCount.textContent = countLabel(total);
    }

    if (empty) {
      empty.hidden = total > 0;
      if (emptyQuery) {
        emptyQuery.textContent = state.query;
      }
    }
  };

  const setQuery = (value) => {
    state.query = value.trim();
    state.words = normalize(value).split(/\s+/).filter(Boolean);
    apply();
  };

  const reset = () => {
    if (input) {
      input.value = '';
    }
    state.status = '';
    setQuery('');
  };

  if (input) {
    input.addEventListener('input', () => setQuery(input.value));
    // Enter ve vyhledávacím poli nemá co odeslat – jen ať prohlížeč nic nedělá
    input.addEventListener('keydown', (event) => {
      if (event.key === 'Enter') {
        event.preventDefault();
      }
    });
  }

  root.querySelector('[data-rd-vd-clear]')?.addEventListener('click', () => {
    setQuery('');
    if (input) {
      input.value = '';
      input.focus();
    }
  });

  root.querySelector('[data-rd-vd-reset]')?.addEventListener('click', reset);

  filters.forEach((button) => {
    button.addEventListener('click', () => {
      state.status = button.dataset.rdVdFilter || '';
      apply();
    });
  });

  groups.forEach((group) => {
    if (!group.more) {
      return;
    }

    const label = group.more.querySelector('[data-rd-vd-more-label]');

    group.more.addEventListener('click', () => {
      group.open = !group.open;
      group.more.setAttribute('aria-expanded', group.open ? 'true' : 'false');
      if (label) {
        label.textContent = group.open ? group.more.dataset.labelHide : group.more.dataset.labelShow;
      }
      apply();
    });
  });

  // Kotva na skupinu nebo sekci, kterou filtr či hledání zrovna schovává (tlačítka v hlavičce,
  // rozcestník, odkaz odjinud) – nejdřív všechno ukázat, pak prohlížeč na cíl doskočí sám.
  document.addEventListener('click', (event) => {
    const link = event.target.closest('a[href^="#"]');
    if (!link || link.hash.length < 2) {
      return;
    }

    const target = document.getElementById(decodeURIComponent(link.hash.slice(1)));
    if (target && root.contains(target) && (target.hidden || target.closest('[hidden]'))) {
      reset();
    }
  });

  // prohlížeč může po návratu zpět vyplnit pole z minula
  if (input && input.value.trim() !== '') {
    setQuery(input.value);
  }

  if (search) {
    search.hidden = false;
  }
  if (status) {
    status.hidden = false;
  }
}
